import React, { useEffect, useState, useMemo } from 'react';

// Comps
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import ProductTile from '@src/stories/ecommerce/Product/ProductTile';
import ProductListPagination from '@src/stories/ecommerce/Product/ProductListPagination';
import { LoadingSpinner } from '@arctheme-components/elements/LoadingSpinner/LoadingSpinner';

// Store
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Utils
import { Button } from '@src/stories/elements/Button/Button';
import shopifyHelpers from 'frontend/js/shopify';

// Types
import { IGraphQlProduct } from '@src/types/product';

export const AccountWishlist = ({ settings, locales }) => {
  // Store
  const localLists = useLocalWishlistStore((state) => state.localLists);

  // States
  const [products, setProducts] = useState<IGraphQlProduct[] | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PRODUCTS_PER_PAGE = 10;

  // Re-fetch products when there are updates in wishlist
  useEffect(() => {
    const fetchProducts = async () => {
      setProducts(null);
      setCurrentPage(1);
      setIsLoadingProducts(true);

      const wishlist = localLists?.[0];

      if (!wishlist || !wishlist.products || wishlist.products.length === 0) {
        setProducts([]);
        setIsLoadingProducts(false);
        return;
      }

      try {
        const graphQlProducts = await shopifyHelpers.getShopifyProductList(wishlist.products);

        setProducts(graphQlProducts);
      } catch (error) {
        console.error('Error fetching wishlist products:', error);
        setProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [localLists]);

  const isLoading = isLoadingProducts;

  // Pagination logic
  const totalPages = products ? Math.ceil(products.length / PRODUCTS_PER_PAGE) : 1;
  const paginatedProducts = products
    ? products.slice((currentPage - 1) * PRODUCTS_PER_PAGE, currentPage * PRODUCTS_PER_PAGE)
    : [];

  return (
    <AccountSectionLayout
      classes={`w-full hide-scrollbar ${
        products && products.length ? 'md:min-h-[400px]' : 'h-[50vh] lg:h-[80vh] justify-center items-center w-full'
      }`}
    >
      {isLoading ? (
        // TODO: Replace with appropriate loader
        <div className='flex items-center gap-3 pointer-events-auto py-6'>
          <span className='b8--medium text-gray-600 animate-pulse'>Loading products...</span>
          <span className='spinner spinner--md'></span>
        </div>
      ) : products && products.length ? (
        <>
          <div
            id='product-grid'
            className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 md:gap-x-2 gap-y-6 md:gap-y-16'
          >
            {paginatedProducts.map((product, index) => (
              <ProductTile
                product={product}
                swatches={product?.swatches}
                siblings={undefined}
                settings={settings}
                globals={undefined}
                type={settings.settings.tile_type}
                enableWishlist={true}
                showPrice={settings.settings.show_price}
                showQuickAdd={settings.settings.show_quickadd}
                showTags={settings.settings.show_tags}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className='flex justify-center mt-8'>
              <ProductListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={isLoading}
                goToPage={(page) => {
                  setCurrentPage(page);
                  setTimeout(() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }, 500);
                }}
                loadMoreProducts={() => {}}
                paginationType='pages'
                paginationTextClass={'b6--bold'}
                paginationActiveColour={'#101010'}
                paginationInactiveColour={'#C2C2C2'}
              />
            </div>
          )}
        </>
      ) : (
        <div className='flex justify-center items-center flex-col w-full'>
          <Paragraph
            type='b7--medium text-center'
            text={locales?.wishlist?.empty || 'Your wishlist is empty.'}
          />
          <Button
            onClick={() => (window.location.href = '/collections/all')}
            className='button button-outline mt-6'
            label='Continue Shopping'
          />
        </div>
      )}
    </AccountSectionLayout>
  );
};
